import React, { RefObject } from 'react'
import { ImageLoadEventData, ImageErrorEventData, NativeSyntheticEvent, Platform } from 'react-native'
import PropTypes from 'prop-types'
import { View, Dimensions, ImageBackground, StyleSheet } from 'react-native'
import { Loading } from './loading'
import { ZoomHoverBox, ZoomHoverBoxPosition } from './zoom-hover-box'
import { ZoomedImage } from './zoomed-image'

const s3URL = 'https://d2in4d3ntxbclu.cloudfront.net/images/700px/'

const boxHeight = 300
const boxWidth = boxHeight * 0.8
 
function usePrevious(value: any) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

type ProductDisplayImageDesktopProps = {
  /** The default SKU to display for the product family. */
  defaultSKU: string,
  /** The main color. */
  accentColor: string,
  /** React ref to the desktop zoomed in image. */
  zoomedImgRef: RefObject<ImageBackground>,
  /** Called when the mouse enter is called. */
  onZoomHoverBoxOpenChange: Function,
  /** Called whenever open is true and mousemove */
  onZoomHoverBoxPositionChange: Function,
  partner: string,
}

export const ProductDisplayImageDesktop = ({defaultSKU, accentColor, zoomedImgRef, onZoomHoverBoxOpenChange, onZoomHoverBoxPositionChange, partner}: ProductDisplayImageDesktopProps) => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const [zoomHoverBoxOpen, setZoomHoverBoxOpen] = React.useState<boolean>(false)
  const [zoomHoverBoxPosition, setZoomHoverBoxPosition] = React.useState<ZoomHoverBoxPosition>({top: 0, left: 0})
  const [imageSize, setImageSize] = React.useState<any>({width: 0, height: 0})
  const containerRef = React.useRef<any>(null)
  const imgRef = React.useRef<any>(null)
  const prevDefaultSKU = usePrevious(defaultSKU)

  React.useEffect(() => {
    if (containerRef?.current && Platform.OS === 'web') {
      setImageSize(getImageSize())

      containerRef.current.id = 'container'
      const container = document.querySelector('#container')
      container?.addEventListener('mouseenter', onMouseEnter as any)
      container?.addEventListener('mouseleave', onMouseLeave as any)
      container?.addEventListener('mousemove', onMouseMove as any)

      window.addEventListener('resize', onResize)
  
      return () => {
        const container = document.querySelector('#container')
        container?.removeEventListener('mouseenter', onMouseEnter as any)
        container?.removeEventListener('mouseleave', onMouseLeave as any)
        container?.removeEventListener('mousemove', onMouseMove as any)        
        window?.removeEventListener('resize', onResize)
      }
    }
  }, [])

  React.useEffect(() => {
    if (prevDefaultSKU !== defaultSKU) {
      setLoading(true)
      setImageSize(getImageSize())
    }
  }, [defaultSKU])

  const onResize = () => {
    setImageSize(getImageSize())
  }

  const getImageSize = () => {
    const height = Dimensions.get('window').height
    const maxHeight = height < 700 ? height < 500 ? 300 : 450 : 650
    const width = Math.floor(maxHeight * 0.8)
    return {width: width, height: maxHeight}
  }

  const onMouseEnter = (e: MouseEvent) => {
    setZoomHoverBoxOpen(true)
    onZoomHoverBoxOpenChange(true)
  }

  const onMouseLeave = (e: MouseEvent) => {
    setZoomHoverBoxOpen(false)
    onZoomHoverBoxOpenChange(false)
  }

  const onMouseMove = (e: MouseEvent) => {
    setZoomHoverBoxPosition({top: e.offsetY, left: e.offsetX})
    onZoomHoverBoxPositionChange({top: e.offsetY, left: e.offsetX})
  }
  
  const styles = StyleSheet.create({
    container: {
      width: imageSize.width,
      height: '100%',
      maxHeight: imageSize.height,
      position: 'relative',
    },
    image: {
      width: '100%',
      margin: 0,
      maxHeight: '100%',
      height: '100%',
    },
  })

  const getHoverBoxPosition = () => {
    const position = {...zoomHoverBoxPosition}
    position.left = getLeftOffset(position)
    position.top = getTopOffset(position)
    return position
  }

  const getLeftOffset = (position: ZoomHoverBoxPosition) => {
    if (position.left <= boxWidth / 2) {
      return 0
    } else if (position.left >= imageSize.width - boxWidth / 2) {
      return imageSize.width - boxWidth
    } else {
      return position.left - boxWidth / 2
    }
  }

  const getTopOffset = (position: ZoomHoverBoxPosition) => {
    if (position.top <= boxHeight / 2) {
      return 0
    } else if (position.top >= imageSize.height - boxHeight / 2) {
      return imageSize.height - boxHeight
    } else {
      return position.top - boxHeight / 2
    }
  }
  
  return (
    <View style={styles.container} ref={containerRef}>
      <ImageBackground
        ref={zoomedImgRef}
        onLoad={(e: NativeSyntheticEvent<ImageLoadEventData>) => setLoading(false)}
        onError={(e: NativeSyntheticEvent<ImageErrorEventData>) => setLoading(false)}
        onLoadEnd={() => setLoading(false)}
        resizeMode={'contain'}
        style={styles.image}
        source={{uri: s3URL + ((partner == 'fisher' || partner == 'fisheredu') ? 'fisher/' : 'standard/') + defaultSKU + '.png'}}
      >
      </ImageBackground>
      {
        zoomHoverBoxOpen &&
        <>
          <ZoomHoverBox 
            accentColor={accentColor}
            imageSize={{width: imageSize.width, height: imageSize.height}} 
            position={getHoverBoxPosition()}
            boxWidth={boxWidth}
            boxHeight={boxHeight}
          />
        </>
      }
      {
        loading === true && 
        <Loading />
      }
    </View>
  )
}

ProductDisplayImageDesktop.propTypes = {
  defaultSKU: PropTypes.string.isRequired,
  accentColor: PropTypes.string.isRequired,
  zoomedImgRef: PropTypes.object.isRequired,
  onZoomHoverBoxOpenChange: PropTypes.func.isRequired,
  onZoomHoverBoxPositionChange: PropTypes.func.isRequired,
}

ProductDisplayImageDesktop.defaultProps = {
  defaultSKU: 'VDHCH-RG-T0-A0-RC-8540',
}
